<template>

   <spinner v-if="!loaded" ></spinner>

    <div class="template-padding container" v-else-if="loaded"  > 
        
        <div class="row" >
            <div class="col-12 text-left" >
                <router-link :to="{name: 'Copywriting Masterclass' }" class="not-active text-left btn btn-transparent border-none btn-sm" > 
                    <font-awesome-icon icon="arrow-circle-left" ></font-awesome-icon> Back 
                </router-link>
            </div>
        </div>
        
        <div class="col-12 col-md-10 offset-md-1 p-0 mt-3" >
            <img src="@/assets/images/copywriting_masterclass_1200x628-01.png" class="w-100" />
        </div>
        
        <div class="text-justify mt-5 mb-3 h3" style="line-height: 1.5;" >

        </div>
        
        <!-- ACADEMY -->
        <div class="accordion mt-5" role="tablist">
            
            <!-- INTRO -->
            <b-card no-body class="mb-1">
                <b-card-header header-tag="header" class="p-2" role="tab">
                    <div block v-b-toggle.accordion-0 class="col-12 cursor-pointer font-size-20 font-weight-bold" >
                        So how does copywriting work?
                        <div class="float-right graymedium-color h6 mt-3 ml-2" > 
                            <span id="accordion0" > {{ calculateRead('accordion0') }} </span>
                            <font-awesome-icon :icon="['fas', 'glasses']" class="" style="margin-top: -3px;" /> 
                        </div>
                    </div>
                </b-card-header>
                <b-collapse id="accordion-0"  accordion="my-accordion" role="tabpanel" class="p-3" >
                    <b-card-body ref="accordion0" >
                        
Nowadays, readers are bombarded by more ads and information overload than at any time in human history.  And the reason they don’t read most is simple: there are just too many ads  competing for people’s attention. 
<br><br>
And they don’t have the time—or the inclination—to read them all.
<br><br>
This is why you, as a copywriter, must work hard to get people’s attention in just a couple of seconds, otherwise they scroll away.
<br><br>
And getting attention is the job of the headline. 
<br><br>
<i>“If you can come up with a good headline, you are almost sure to have a good ad, but even the greatest writer can’t save an ad with a poor headline.”</i> writes John Caples.
<br><br>
In all forms of advertising, the “first impression” - the first thing the reader sees, reads, or hears - can mean the difference between success and failure. 
<br><br>
If the first impression is boring or irrelevant, the ad will not attract your prospect. If it offers news or helpful information or promises a reward for reading the ad, the first impression will win the reader’s attention. 
<br><br>
And this is the first step in persuading the reader to buy your product and invite him into your story.
<br><br>
After you’ve managed to get the reader’s attention and cast him into the story, you need to keep  their interest up, keep them engaged.
<br><br>
Every sentence must sell the next.
<br><br>
Next, I want to share with you 11 tips for writing clear copy.
                        
                    </b-card-body>
                </b-collapse>
            </b-card>
            <!-- INTRO -->
            
            <!-- Structure -->
            <b-card no-body class="mb-1">
                <b-card-header header-tag="header" class="p-2" role="tab">
                    <div block v-b-toggle.accordion-1 class="col-12 cursor-pointer font-size-20 font-weight-bold" >
                        10 Tips for Writing Clear Copy 
                        <div class="float-right graymedium-color h6 mt-3 ml-2" > 
                            <span id="accordion1" > {{ calculateRead('accordion1') }} </span>
                            <font-awesome-icon :icon="['fas', 'glasses']" class="" style="margin-top: -3px;" /> 
                        </div>
                    </div>
                </b-card-header>
                <b-collapse id="accordion-1"  accordion="my-accordion" role="tabpanel" class="p-3" >
                    <b-card-body ref="accordion1">
                        
<br><h2 class="font-size-16 font-weight-bold" >1. Put the Reader First </h2>
Always think of the reader, always put him first. 
<br><br>
Ask yourself: <br>
- Will the person reading this understand what I have written? <br>
- Does he know the special terminology I have used? <br>
- Does my copy tell her something important or new or useful? <br>
- If I were the reader, would this copy persuade me to buy the product? <br>
<br><br>
One technique to help you write for the reader is to address him directly as “you” in the copy, it will feel like it’s talking to him directly.
<br><br>
Copywriters call this the “you-orientation.” Flip through a magazine, and  you’ll see that 90 percent of the ads contain the word you in the body copy.
<br><br>

<br><h2 class="font-size-16 font-weight-bold" >2. Carefully Organize Your Selling Points </h2>
When you write your copy, you must carefully organize the points you want to make. Usually, there’s one primary sales message and several secondary messages.
<br><br>
The headline states the main selling proposition, and the first few paragraphs expand on it. Secondary points can be covered later in the body copy. 
<br><br>
If this copy is long, each secondary point may get a separate heading or number.
<br><br>
<a href="https://tppr.me/Htnl" target="_blank" class="font-700 text-underline" >See example</a>
<br><br>

<br><h2 class="font-size-16 font-weight-bold" >3. Break the Writing into Short Sections </h2>
If the content of your ad can be organized as a series of sales points, you can cover each point in a separate section of copy. 
<br><br>
This isn’t necessary in short ads of 150 or fewer words. But as length increases, copy becomes more difficult to read. 
<br><br>
Breaking the text into several short sections makes it easier to read.
<br><br>
Paragraphs should also be kept short. Long, unbroken chunks of type intimidate readers. A page filled with a solid column of tiny type says, “This is going to be tough to read!”.
<br><br>

<br><h2 class="font-size-16 font-weight-bold" >4. Use Short Sentences </h2>
Short sentences are easier to read than long sentences. All professional writers  - newspaper reporters, publicists, magazine writers, copywriters - are taught to write in crisp, short, snappy sentences. 
<br><br>
Long sentences tire and puzzle your readers. By the time they have gotten to the end of a lengthy sentence, they don’t remember what was at the beginning.
<br><br>
How can you reduce the length of the sentences? Try to break large sentences into two or more separate sentences or use punctuation to divide them into 2 parts.
<br><br>

<br><h2 class="font-size-16 font-weight-bold" >5. Use Small, Simple Words </h2>
Simple words communicate more effectively than big words. People use big words to impress others, but they rarely do. More often, big words annoy and distract the reader from what the writer is trying to say. 
<br><br>
Yet big words persist, because using pompous language makes the reader or speaker feel important.
<br><br>
In advertising copy, you are trying to communicate with people, not impress them or boost your own ego. Avoid pompous words and fancy phrases. Try to speak the reader’s language.
<br><br>

<br><h2 class="font-size-16 font-weight-bold" >6. Avoid Technical Jargon  </h2>
Industrial copy isn’t the only writing that uses technical jargon.
<br><br>




There’s a famous example, and I’m pretty sure you’ve heard of this company: Apple. Apple is a genius now at selling their products because they are all customer focused. But like every company, they started from the bottom. And when they released their first computer, in 1976, they released an ad talking about the product specs, which was basically 9 full paragraphs of geek talk, which, at that time, probably a hand-full of people knew what it meant …
<br><br>
It was no wonder nobody was interested. I mean who would understand that, in 1976? It was only after Jobs returned to Apple when it started shifting the company to be more customer oriented.
<br><br>
The point is: don’t use jargon when writing to an audience that doesn’t speak your special language. Jargon is useful for communicating within a small group of experts. But used in copy aimed at outsiders, it confuses the reader and obscures the selling message
<br><br>
                        
<br><h2 class="font-size-16 font-weight-bold" >7. Be Concise </h2>
Good copy is concise. Unnecessary words waste the reader’s time, dilute  the sales message, and take up space that could be put to better use. 
<br><br>
Rewriting is the key to producing concise copy. 
<br><br>
When you write your first draft, the words just flow, and you can’t help being chatty. In the editing stage, unnecessary words are deleted to make the writing sparkle with vigor and clarity.
<br><br>
A tip for re-writing your copy: try to attack each sentence of the copy. If it doesn’t sell the next, maybe it shouldn’t be there.
<br><br>
Make your writing concise. Avoid redundancies, run-on sentences, wordy phrases, the passive voice, unnecessary adjectives, and other poor stylistic habits that take up space but add little to meaning or clarity. Edit your writing to remove unnecessary words, phrases, sentences, and paragraphs.
<br><br>
                        
<br><h2 class="font-size-16 font-weight-bold" >8. Be Specific </h2>
Advertising persuades us by giving specific information about the product being advertised. The more facts you include in your copy, the better. 
<br><br>
Copywriters who don’t bother to dig for specifics produce vague, weak, meaningless copy that gets lost in the noise.
<br><br>
                        
<br><h2 class="font-size-16 font-weight-bold" >9. Go Straight to the Point </h2>
If the headline is the most important part of an ad, then the lead paragraph is surely the second most important part. It is this lead that either lures the reader into the text by fulfilling the promise of the headline, or bores the reader with uninteresting, irrelevant, unnecessary words.
<br><br>
                        
<br><h2 class="font-size-16 font-weight-bold" >10. Write in a Friendly, Conversational Style </h2>
Conversational tone is especially important in advertising, where the printed page is a substitute for a salesperson. 
<br><br>
A light, conversational style is much easier to read than the stiff, formal prose of business, science, and academia. And when you write simply, you become the reader’s friend.
<br><br>
Try to:<br>
- Use pronouns: I, we, you, they<br>
- Use colloquial expressions: a sure thing, turn-on, rip-off, okay<br>
- Use contractions: they’re, you’re, it’s, here’s, we’ve, I’m

                        
                    </b-card-body>
                </b-collapse>
            </b-card>
            <!-- Structure -->
                    
            <!-- Structure -->
            <b-card no-body class="mb-1">
                <b-card-header header-tag="header" class="p-2" role="tab">
                    <div block v-b-toggle.accordion-2 class="col-12 cursor-pointer font-size-20 font-weight-bold" >
                        Copywriter’s Checklist
                        <div class="float-right graymedium-color h6 mt-3 ml-2" > 
                            <span id="accordion2" > {{ calculateRead('accordion2') }} </span>
                            <font-awesome-icon :icon="['fas', 'glasses']" class="" style="margin-top: -3px;" /> 
                        </div>
                    </div>
                </b-card-header>
                <b-collapse id="accordion-2"  accordion="my-accordion" role="tabpanel" class="p-3" >
                    <b-card-body ref="accordion2">
                        
Before you release your copy or publish it, ask yourself these questions:
<br><br>
<strong class="font-size-16 font-700 pl-3" >1. Does the copy fulfill the promise of the headline?</strong><br>
Copy that doesn’t fulfill the promise of the headline cheats the reader - and the reader knows it. 
<br><br>
<strong class="font-size-16 font-700 pl-3" >2. Is the copy interesting? </strong><br>
Your copy can’t generate enthusiasm for the product if the reader yawns as she reads it. Tell a story, give news and improve the reader’s life. Make it interesting. 
<br><br>
<strong class="font-size-16 font-700 pl-3" >3. Is it easy to read? </strong><br>
When a person reads your copy, it is not his job to try to figure out what you mean. It is your job to explain what you mean in plain, simple English. 
<br><br>
Use short sentences, short paragraphs and small words. Be clear. 
<br><br>
<strong class="font-size-16 font-700 pl-3" >4. Is it believable? </strong><br>
People mistrust advertising and advertising professionals. You must work hard to convince the reader that what you say is true. 
<br><br>
One way to establish credibility is to include testimonials from satisfied customers. Another is to offer a demonstration or scientific evidence that proves your claim. 
<br><br>
But always tell people the truth. 
<br><br>
<strong class="font-size-16 font-700 pl-3" >5. Is it persuasive?</strong><br>
Clear, readable copy is not enough. Your copy must sell as well as communicate. To sell, your copy must get attention, hook the reader’s interest, create a desire for the product, prove the product’s superiority and ask them to take action.
<br><br>
<strong class="font-size-16 font-700 pl-3" >6. Is it specific?</strong><br>
To persuade people to buy, you have to give them specifics—facts, features, benefits, savings - reasons why they should buy the product. 
<br><br>
The more specific you are, the more informative and believable your copy. 
<br><br>
<strong class="font-size-16 font-700 pl-3" >7. Is it concise? </strong><br>
Tell the whole story in as few words as possible. When you are finished, stop. 
<br><br>
<strong class="font-size-16 font-700 pl-3" >8. Is it relevant? </strong><br>
Make sure your copy discusses facts that are relevant to the reader’s self-interest. 
<br><br>
Don’t talk about yourself. Don’t tell the reader what you did, achieved, what you like or dislike. That’s not what he wants to hear. What’s important to him is what he likes, what he needs, what he wants.
<br><br>                        
<strong class="font-size-16 font-700 pl-3" >9. Does it flow smoothly?  </strong><br>
Good copy flows smoothly from one point to the next. There are no confusing arguments and no strange terms that break the flow. 
<br><br>
Each sentence must sell the next.
<br><br>
<strong class="font-size-16 font-700 pl-3" >10. Does it call for action?  </strong><br>
People don’t usually take action by themselves, they need to be challenged.
<br><br>
So make sure you include a strong call to action that challenges them.
                        
                    </b-card-body>
                </b-collapse>
            </b-card>
            <!-- Structure -->
                    
        
          </div>
        <!-- ACADEMY -->

        
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Cookies from 'js-cookie'
import axios from 'axios'
    
export default{
    name: "ACADEMY_sales_page",
    props:["APPKEY"],
    components:{
    },
    data () {
        return {
            loaded: false,
            text: `
              Anim pariatur cliche reprehenderit, enim eiusmod high life accusamus terry
              richardson ad squid. 3 wolf moon officia aute, non cupidatat skateboard dolor
              brunch. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon
              tempor, sunt aliqua put a bird on it squid single-origin coffee nulla
              assumenda shoreditch et. Nihil anim keffiyeh helvetica, craft beer labore
              wes anderson cred nesciunt sapiente ea proident. Ad vegan excepteur butcher
              vice lomo. Leggings occaecat craft beer farm-to-table, raw denim aesthetic
              synth nesciunt you probably haven't heard of them accusamus labore VHS.
            `
        }
    },
    mounted(){
        this.loaded = true;
    },
    methods:{
        openRow(params){
            
            let user =  Cookies.get('user')
            user = JSON.parse( atob( user ) )
            
            let page = params.row.Icon;
            let ID = params.row.ID;
            
            
            
            
        },
        returnDate(a){
            //return this.$moment.unix(a).format( 'MMM D, HH:mm' )
            return helper.formatDate( a, 'MMM D, HH:mm' )
        },
        
        calculateRead( target ){
            
            //console.log( ev )
            setTimeout( function(){
                
                //console.log( this.$refs )
                //console.log( this.$refs[target].innerHTML )                
                
                let ev = this.$refs[target].innerHTML
                
                const wordsPerMinute = 150; // Average case.
                let result;
                
                let textLength = ev.split(" ").length; // Split by words
                if(textLength > 0){
                    let value = Math.ceil(textLength / wordsPerMinute);
                    result = `~${value} min read`;
                }
                //return result
                //this.$refs[target].appendChild( result )
                document.getElementById(target).innerHTML = result;
                
            }.bind(this), 400 )
                
        }
    },
    computed: {

    },
    created: function () {

    },
    
}
</script>

<style scoped>

</style>


// WEBPACK FOOTER //
// src/templates/Academy/courses/copywriting-masterclass/02.vue